import React, { useEffect, useMemo } from 'react';
import { sanitizeEncryptedPiiBlocks } from '../lib/piiDisplay';
import ReactDOM from 'react-dom';
import { X, User, CreditCard, BookOpen, Briefcase, Users, MapPin, Phone, Mail, Calendar, Shield, CheckCircle, Clock, AlertCircle, Globe, Heart } from 'lucide-react';
import MasterPipeline, { MASTER_STAGES } from './MasterPipeline';
import { usePrograms } from '../hooks/usePrograms';
import { getPipelineStages, stageLabel as getStageLabel } from '../lib/programPipeline';
import { useModalLayer } from '../lib/modalStack';
import { normalizePassportDates } from '../lib/applicationSchema';
import { Application } from '../types';

interface ApplicantPreviewModalProps {
  app: Application | null;
  onClose: () => void;
  onOpenFull?: (app: Application) => void;
  /** centered = front-and-centre modal (matches the main Events page preview);
   *  default = right slide-in panel. */
  centered?: boolean;
}

export default function ApplicantPreviewModal({ app: rawApp, onClose, onOpenFull, centered }: ApplicantPreviewModalProps) {
  // Strip any still-encrypted PII blocks before rendering: this modal
  // renders personalDetails / passportImmigration / emergencyContacts
  // fields directly, and an {iv, ciphertext, authTag} object as a React
  // child crashes with error #31. Sanitized fields become undefined and
  // every field falls back to its normal '—' empty state.
  const app = useMemo(() => sanitizeEncryptedPiiBlocks(rawApp), [rawApp]);
  const zIndex = useModalLayer(app != null, onClose);
  if (!app) return null;

  // Prevent body scrolling
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const pd = app.personalDetails;
  const pp = normalizePassportDates(app.passportImmigration || {});
  const { programs } = usePrograms();
  const matchedProg = (programs || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
  const pipelineStages = getPipelineStages(matchedProg);
  const getSubStatus = (a: any) => a ? (a as any).submissionStatus || a.status || 'Draft' : 'Draft';
  const stageLabelText = getStageLabel(pipelineStages, (app as any).masterStage, getSubStatus(app));
  const isActive = (app as any)?.activeStatus !== false;

  const submissionStatus = getSubStatus(app);
  const submissionColor =
    submissionStatus === 'Submitted' ? 'bg-brand-active border border-blue-100' :
    submissionStatus === 'Draft' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
    submissionStatus === 'Returned' ? 'bg-orange-50 text-orange-700 border border-orange-100' :
    submissionStatus === 'Cancelled' ? 'bg-red-50 text-red-700 border border-red-100' :
    'bg-slate-100 text-slate-600 border border-slate-200';

  return ReactDOM.createPortal(
    // Backdrop
    <div
      className={`fixed inset-0 ${centered ? 'bg-black/50 flex items-center justify-center p-4' : 'bg-black/40'} animate-fade-in`}
      style={{ zIndex }}
      onClick={onClose}
    >
      {/* Panel — centered (front-and-centre, like main Events preview) or slide-in */}
      <div
        className={centered
          ? 'bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden'
          : 'absolute right-0 top-0 min-h-full w-full max-w-lg bg-white shadow-2xl flex flex-col animate-slide-in-right overflow-hidden'}
        onClick={(e) => e.stopPropagation()}
        style={centered ? undefined : { animation: 'slideInRight 0.25s cubic-bezier(0.16,1,0.3,1) both' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 rounded-xl border-2 border-white/30 overflow-hidden bg-white/10 flex items-center justify-center shrink-0">
              {app.profilePhotoUrl
                ? <img src={app.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
                : <span className="text-white font-bold text-lg">{pd?.firstName?.charAt(0) || 'A'}</span>
              }
            </div>
            <div>
              <h3 className="text-base font-bold text-white font-display leading-tight">
                {pd?.firstName || ''} {pd?.lastName || 'Unnamed Applicant'}
              </h3>
              <p className="text-caption text-white/70 font-medium">{app.userEmail}</p>
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
                <span className="text-caption bg-white/15 text-white border border-white/20 px-2 py-0.5 rounded font-bold tracking-wider">{app.programType}</span>
                {app.programTerm ? <span className="text-caption bg-white/15 text-white border border-white/20 px-2 py-0.5 rounded font-bold">Term: {app.programTerm}</span> : null}
                {(app as any).resumeUploaded ? <span title="Resume on file" className="text-caption bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 px-2 py-0.5 rounded font-bold">📄 Resume</span> : null}
                {(app as any).requirementsConfirmed ? <span title="Requirements confirmed" className="text-caption bg-blue-400/20 text-blue-200 border border-blue-400/30 px-2 py-0.5 rounded font-bold">✓ Reqs</span> : null}
                <span className="text-caption bg-white/10 text-white/80 border border-white/20 px-2 py-0.5 rounded font-bold"># {app.applicationId}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl transition-all cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Status + pipeline */}
        <div className="px-6 pt-4 pb-2 border-b border-slate-100 shrink-0 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-caption uppercase font-bold text-slate-400 tracking-wider">Current Stage</span>
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} title={isActive ? 'Active' : 'Inactive'} />
              <span className={`px-3 py-1 text-caption font-bold rounded-full border ${submissionColor}`}>{submissionStatus}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-caption uppercase font-bold text-slate-400 tracking-wider">Master Phase</span>
            <span className="text-caption font-bold text-slate-700">{stageLabelText}</span>
          </div>
          <MasterPipeline currentStage={(app as any).masterStage ?? 0} readOnly={true} stages={pipelineStages} />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Personal */}
          {pd && (
            <section className="space-y-3">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><User className="h-3.5 w-3.5" /><span>Personal</span></h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Date of Birth</span><p className="font-semibold text-slate-800">{pd.dob || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Gender</span><p className="font-semibold text-slate-800">{pd.gender || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Citizenship</span><p className="font-semibold text-slate-800">{pd.countryOfCitizenship || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Country of Birth</span><p className="font-semibold text-slate-800">{pd.countryOfBirth || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Phone</span><p className="font-semibold text-slate-800">{pd.phone || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Marital Status</span><p className="font-semibold text-slate-800">{pd.maritalStatus || '—'}</p></div>
                <div className="col-span-2"><span className="text-slate-400 block text-caption font-bold uppercase">Address</span><p className="font-semibold text-slate-800">{[pd.address?.street, pd.address?.city, pd.address?.state, pd.address?.zip, pd.address?.country].filter(Boolean).join(', ') || '—'}</p></div>
              </div>
            </section>
          )}

          {/* Criminal / Health flags */}
          {(pd?.criminalConviction !== undefined || pd?.insurancePreexistingAcknowledged !== undefined) && (
            <section className="border-t border-slate-100 pt-4 space-y-1">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><Shield className="h-3.5 w-3.5" /><span>Background & Health</span></h4>
              <div className="flex flex-wrap gap-2 text-xs pt-1">
                {pd.criminalConviction !== undefined && (
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg font-bold ${pd.criminalConviction ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                    {pd.criminalConviction ? '⚠️ Criminal history disclosed' : '✓ No criminal history'}
                  </span>
                )}
                {pd.insurancePreexistingAcknowledged !== undefined && (
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg font-bold ${pd.insurancePreexistingAcknowledged ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                    {pd.insurancePreexistingAcknowledged ? '✓ Insurance pre-existing acknowledged' : '⚠️ Insurance pre-existing not acknowledged'}
                  </span>
                )}
              </div>
            </section>
          )}

          {/* Passport */}
          {pp && (
            <section className="space-y-3 border-t border-slate-100 pt-4">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><CreditCard className="h-3.5 w-3.5" /><span>Passport & Immigration</span></h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="col-span-2"><span className="text-slate-400 block text-caption font-bold uppercase">Passport #</span><p className="font-mono font-semibold text-slate-800">{pp.passportNumber || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Country</span><p className="font-semibold text-slate-800">{pp.passportCountry || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Issue Date</span><p className="font-semibold text-slate-800">{pp.issueDate || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Expires</span><p className="font-semibold text-slate-800">{pp.expirationDate || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Visa Status</span><p className="font-semibold text-slate-800">{pp.visaStatus || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">DS-2019 #</span><p className="font-semibold text-slate-800">{pp.ds2019Number || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">Intended Arrival</span><p className="font-semibold text-slate-800">{pp.intendedArrival || '—'}</p></div>
                <div><span className="text-slate-400 block text-caption font-bold uppercase">US Destination</span><p className="font-semibold text-slate-800">{pp.usDestination || '—'}</p></div>
              </div>
            </section>
          )}

          {/* Program Preferences */}
          {app.programPreferences && (app.programPreferences.preferredIndustry || app.programPreferences.preferredState || app.programPreferences.englishProficiency) && (
            <section className="space-y-2 border-t border-slate-100 pt-4">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><Globe className="h-3.5 w-3.5" /><span>Program Preferences</span></h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {app.programPreferences.preferredIndustry && <div><span className="text-slate-400 block text-caption font-bold uppercase">Industry</span><p className="font-semibold text-slate-800">{app.programPreferences.preferredIndustry}</p></div>}
                {app.programPreferences.preferredState && <div><span className="text-slate-400 block text-caption font-bold uppercase">State</span><p className="font-semibold text-slate-800">{app.programPreferences.preferredState}</p></div>}
                {app.programPreferences.startDateAvailability && <div><span className="text-slate-400 block text-caption font-bold uppercase">Available From</span><p className="font-semibold text-slate-800">{app.programPreferences.startDateAvailability}</p></div>}
                {app.programPreferences.englishProficiency && <div><span className="text-slate-400 block text-caption font-bold uppercase">English</span><p className="font-semibold text-slate-800">{app.programPreferences.englishProficiency}</p></div>}
                <div className="col-span-2"><span className="text-slate-400 block text-caption font-bold uppercase">Relocate</span><p className="font-semibold text-slate-800">{app.programPreferences.willingnessToRelocate !== undefined ? (app.programPreferences.willingnessToRelocate ? 'Yes' : 'No') : '—'}</p></div>
              </div>
            </section>
          )}

          {/* Emergency Contacts */}
          {app.emergencyContacts && app.emergencyContacts.length > 0 && (
            <section className="space-y-2 border-t border-slate-100 pt-4">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><Heart className="h-3.5 w-3.5" /><span>Emergency Contacts ({app.emergencyContacts.length})</span></h4>
              {app.emergencyContacts.slice(0, 1).map((c: any) => (
                <div key={c.id} className="bg-rose-50 rounded-xl p-3 text-xs border border-rose-100">
                  <p className="font-bold text-rose-800">{c.fullName}</p>
                  <p className="text-rose-600 mt-0.5">{c.relationship}</p>
                  <p className="text-rose-500 mt-0.5">{c.phone}{c.email ? ` · ${c.email}` : ''}</p>
                </div>
              ))}
              {app.emergencyContacts.length > 1 && (
                <p className="text-caption text-slate-400 font-medium">+{app.emergencyContacts.length - 1} more contact(s)</p>
              )}
            </section>
          )}

          {/* Education summary */}
          {app.educationalBackground && app.educationalBackground.length > 0 && (
            <section className="space-y-2 border-t border-slate-100 pt-4">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><BookOpen className="h-3.5 w-3.5" /><span>Education ({app.educationalBackground.length})</span></h4>
              {app.educationalBackground.slice(0, 2).map((ed: any) => (
                <div key={ed.id} className="bg-slate-50 rounded-xl p-3 text-xs border border-slate-100">
                  <p className="font-bold text-slate-800">{ed.degreeEarned || ed.degree || ''} — {ed.fieldOfStudy || ed.field || ''}</p>
                  <p className="text-slate-500 mt-0.5">{ed.institutionName || ed.institution || ed.school || ''}</p>
                </div>
              ))}
              {app.educationalBackground.length > 2 && onOpenFull && (
                <button onClick={() => onOpenFull(app)} className="text-caption text-brand font-semibold cursor-pointer mt-1">
                  +{app.educationalBackground.length - 2} more
                </button>
              )}
            </section>
          )}

          {/* Work summary */}
          {app.workExperience && app.workExperience.length > 0 && (
            <section className="space-y-2 border-t border-slate-100 pt-4">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><Briefcase className="h-3.5 w-3.5" /><span>Work Experience ({app.workExperience.length})</span></h4>
              {app.workExperience.slice(0, 2).map((w: any) => (
                <div key={w.id} className="bg-slate-50 rounded-xl p-3 text-xs border border-slate-100">
                  <p className="font-bold text-slate-800">{w.jobTitle || w.role || ''} @ {w.employerName || w.employer || ''}</p>
                  <p className="text-slate-500 mt-0.5">{w.startDate} – {w.endDate}</p>
                </div>
              ))}
              {app.workExperience.length > 2 && onOpenFull && (
                <button onClick={() => onOpenFull(app)} className="text-caption text-brand font-semibold cursor-pointer mt-1">
                  +{app.workExperience.length - 2} more
                </button>
              )}
            </section>
          )}

          {/* Assignment */}
          {app.assignment && (
            <section className="space-y-2 border-t border-slate-100 pt-4">
              <h4 className="text-caption uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1.5"><MapPin className="h-3.5 w-3.5" /><span>Placement</span></h4>
              <div className="bg-blue-50 rounded-xl p-3 text-xs border border-blue-100">
                <p className="font-bold text-blue-800">{app.assignment.employerName}</p>
                <p className="text-brand mt-0.5">{app.assignment.roleTitle} · {app.assignment.city}, {app.assignment.state}</p>
                {app.assignment.country && <p className="text-blue-500 mt-0.5">{app.assignment.country}</p>}
              </div>
            </section>
          )}

          {/* Returned note */}
          {(getSubStatus(app) === 'Returned') && (app as any).returnNote && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-900">Returned for editing</p>
                <p className="text-caption text-amber-700 mt-1">{(app as any).returnNote}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
          <div className="text-caption text-slate-400 font-medium">
            <span>Updated: {new Date(app.updatedAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all cursor-pointer">Close</button>
            {onOpenFull && (
              <button onClick={() => { onOpenFull(app); onClose(); }} className="px-4 py-2 text-xs font-bold btn-primary  text-white rounded-xl transition-all shadow-sm cursor-pointer">Open Full Profile →</button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
}
